import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'

interface MotifProps {
    active: boolean
}

const SPEED_LINES = [14, 32, 50]

// Launch fast: speed lines that stretch out and an arrow that darts forward when the path is chosen.
export const LaunchMotif: FC<MotifProps> = ({ active }): ReactNode => {
    return (
        <motion.svg
            aria-hidden='true'
            viewBox='0 0 120 64'
            className='h-12 w-24 overflow-visible sm:h-14 sm:w-28'
            initial={false}
            animate={active ? 'active' : 'rest'}
        >
            {SPEED_LINES.map((y, index) => (
                <motion.line
                    key={y}
                    x1={6 + index * 8}
                    x2={56}
                    y1={y}
                    y2={y}
                    stroke='currentColor'
                    strokeWidth={6}
                    strokeLinecap='round'
                    variants={{
                        rest: { pathLength: 0.5, x: 0, opacity: 0.5 },
                        active: { pathLength: 1, x: -10, opacity: 1, transition: { delay: index * 0.05, duration: 0.45, ease: [0.22, 1, 0.36, 1] } }
                    }}
                />
            ))}

            <motion.path
                d='M70 10 L104 32 L70 54'
                fill='none'
                stroke='currentColor'
                strokeWidth={8}
                strokeLinecap='round'
                strokeLinejoin='round'
                variants={{
                    rest: { x: 0 },
                    active: { x: 12, transition: { type: 'spring', stiffness: 420, damping: 12 } }
                }}
            />
        </motion.svg>
    )
}

const COLUMNS = [0, 1, 2, 3]
const ROWS = [0, 1, 2, 3]
const REST_HEIGHTS = [1, 2, 2, 3]
const ACTIVE_HEIGHTS = [2, 3, 4, 4]

// Built to scale: building blocks that stack higher, like a system growing with the business.
export const ScaleMotif: FC<MotifProps> = ({ active }): ReactNode => {
    return (
        <motion.svg
            aria-hidden='true'
            viewBox='0 0 64 64'
            className='size-12 overflow-visible sm:size-14'
            initial={false}
            animate={active ? 'active' : 'rest'}
        >
            {COLUMNS.map(column => ROWS.map(row => (
                <motion.g
                    key={`${column}-${row}`}
                    variants={{
                        rest: {
                            opacity: row < REST_HEIGHTS[column] ? 1 : 0,
                            y: row < REST_HEIGHTS[column] ? 0 : -6
                        },
                        active: {
                            opacity: row < ACTIVE_HEIGHTS[column] ? 1 : 0,
                            y: row < ACTIVE_HEIGHTS[column] ? 0 : -6,
                            transition: { delay: (column + row) * 0.05, type: 'spring', stiffness: 500, damping: 20 }
                        }
                    }}
                >
                    <rect
                        x={2 + column * 16}
                        y={49 - row * 16}
                        width={13}
                        height={13}
                        rx={2}
                        fill='currentColor'
                    />
                </motion.g>
            )))}
        </motion.svg>
    )
}
